/**
 * LiveRoomPage - Deep-link route /room/:artistId
 * Renders the full LiveRoom experience (with ActionBar + DropOverlay) for a
 * specific artist so shared room links open the real room.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import LiveRoom from '../components/LiveRoom';
import { StoryViewer } from '../components/Stories';
import { useAudioPlayer } from '../context/AudioPlayerContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveRoomPage = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { currentTrack, isPlaying, playTrack, pause } = useAudioPlayer();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStoryViewer, setShowStoryViewer] = useState(null);

  const loadRoom = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [tracksRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/api/artists/tracks/${artistId}`, { headers }).catch(() => null),
        axios.get(`${API_URL}/api/artists/profile/${artistId}`, { headers }).catch(() => null)
      ]);

      const tracks = tracksRes?.data?.tracks || [];
      const profile = profileRes?.data?.profile || profileRes?.data || {};
      const first = tracks[0] || {};

      setRoom({
        id: artistId,
        artistName: profile.stage_name || first.artist_name || 'Artist',
        artistImage: profile.profile_picture || first.artist_profile_picture || first.artwork,
        artistUserId: artistId,
        isLive: false,
        tracks,
        genre: first.genre || 'hip-hop',
        listenerCount: 0
      });
    } catch (e) {
      setRoom({ id: artistId, artistName: 'Artist', artistUserId: artistId, tracks: [] });
    } finally {
      setLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    loadRoom();
  }, [loadRoom]);

  const isRoomPlaying = room?.tracks?.some((t) => t.id === currentTrack?.id) && isPlaying;

  const handlePlay = () => {
    if (room?.tracks?.length) {
      playTrack(room.tracks[0], room.tracks, 0);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-[#050505]" data-testid="live-room-page">
      <button
        onClick={() => navigate('/')}
        className="fixed top-4 left-4 z-40 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        data-testid="live-room-back-btn"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <LiveRoom
        room={room}
        isPlaying={isRoomPlaying}
        onPlay={handlePlay}
        onPause={pause}
        energyLevel={50}
        listeners={[]}
        chatMessages={[]}
        currentTrack={currentTrack || room?.tracks?.[0]}
        onViewStory={(storyUser) => setShowStoryViewer(storyUser)}
        interfaceMode="classic"
      />

      {showStoryViewer && (
        <StoryViewer
          storyUser={showStoryViewer}
          onClose={() => setShowStoryViewer(null)}
        />
      )}
    </div>
  );
};

export default LiveRoomPage;
